/**
 * TripDayMealsTable — Kalendertage einer Dienstreise mit Mahlzeiten-/
 * Übernachtungs-Erfassung (RK-7) und optionaler Land-/Region-Auswahl (RK-15,
 * architecture-reisekosten.md §13.8).
 *
 * Generiert `days[]` automatisch aus Ab-/Rückreisedatum (lückenlos, ein
 * Eintrag je Kalendertag — Vertrag §4.1) und lässt je Tag Frühstück/Mittag/
 * Abendessen „gestellt" sowie Übernachtung erfassen. Der letzte Reisetag
 * erzwingt `overnight = false` (die Reise endet an diesem Tag — Vertrag,
 * `travelValidation.validateDaysCoverage`) und ist dafür nicht editierbar.
 *
 * Diese Auto-Generierung ist reine UI-Komfort-Logik — die eigentliche,
 * fail-closed geprüfte Deckung läuft unabhängig davon nochmal in
 * `travelAllowance.ts` (Kern) und `travelValidation.ts` (Schreibgrenze,
 * Trust-Boundary §3.3/V13). Ein hier erzeugtes `days[]` kann daher nie um die
 * echte Validierung „herumkommen".
 *
 * Auslands-Zweig (RK-15, `foreignEnabled` — Default `false`, fail-closed):
 * bei geschlossenem Sub-Flag-Gate bleibt jeder Tag hart auf `countryCode: 'DE'`
 * ohne Land-/Region-Auswahl — Null-Delta zum MVP-Bestand. Nur bei offenem Gate
 * erscheinen zwei zusätzliche Spalten (Land, Ort/Region — Region nur, wenn das
 * gewählte Land Regions-Einträge im übergebenen `foreignRates`-Asset hat).
 *
 * ⚠️ LANDMINE (§13.8/§13.10, Blast-Radius): der Regenerierungs-Merge unten
 * (Datums-Änderung) UND der `changed`-Vergleich führen `regionKey` EXPLIZIT mit —
 * sonst würde eine bereits getroffene Land-/Region-Auswahl bei jeder
 * Ab-/Rückreisedatum-Änderung still gedroppt (bereits erfasste Tage bleiben
 * erhalten, aber ohne diesen Fix eben OHNE ihre Region).
 *
 * RK-17 (architecture-reisekosten.md §14.4, reine UI-Klarheit — keine Rechenkern-
 * Änderung): zusätzliche thead-Gruppenkopfzeile „Vom Arbeitgeber/Hotel gestellt?"
 * (`colSpan=3`) über den drei Mahlzeiten-Spalten + Erklärzeile unter dem Titel
 * (`days.mealsProvidedHint`, Muster `days.overnightHint`). Je Mahlzeiten-Checkbox
 * ein `title`-Tooltip mit dem konkreten Tages-Kürzungsbetrag via
 * `getMealReductionPreviewCents` (`travelUiHints.ts` — nutzt dieselben Resolver
 * wie der Rechenkern, reine Anzeige); fail-closed `null` ⇒ Tooltip zeigt nur die
 * gesetzlich fixen Prozente (20/40/40). `foreignRates` ist bereits ein bestehendes
 * Prop dieser Komponente — keine neue Verdrahtung in `TravelExpensesPage` nötig.
 *
 * @module components/travel/TripDayMealsTable
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { TravelDayRecord } from '../../utils/travel/travelCalcTypes';
import type { TravelForeignRatesConfig } from '../../utils/travel/travelRates';
import { getMealReductionPreviewCents } from '../../utils/travel/travelUiHints';
import type { MealReductionPreviewCents } from '../../utils/travel/travelUiHints';
import { centsToEuros } from '../../utils/moneyUtils';

interface TripDayMealsTableProps {
  departureDateISO: string;
  returnDateISO: string;
  days: TravelDayRecord[];
  onChange: (days: TravelDayRecord[]) => void;
  disabled?: boolean;
  /**
   * NEU (RK-15, §13.8): schaltet die Land-/Region-Auswahl je Reisetag frei. Default `false`
   * (fail-closed) — ohne dieses Prop bleibt die Komponente reines MVP-Inland-Verhalten
   * (jeder Tag hart `'DE'`, keine Auswahl sichtbar). Wird von `TravelExpensesPage` über
   * `canUseForeignTravel(uid, tier)` verdrahtet — NIE eine eigene Gate-Logik hier.
   */
  foreignEnabled?: boolean;
  /**
   * NEU (RK-15): Regions-Datenquelle für die Land-/Region-Auswahl (Länderliste + BMF-Städte-
   * Labels). Explizite Prop-Übergabe statt verstecktem Import des generierten Assets — hält die
   * Komponente mit synthetischen Fixtures testbar (Muster E9, architecture-reisekosten.md §13.1).
   * Nur relevant, wenn `foreignEnabled` true ist.
   */
  foreignRates?: TravelForeignRatesConfig;
}

/** Baut die Länder-Auswahlliste ('DE' + alle im Asset bekannten Länder, dedupliziert, sortiert). */
function buildCountryOptions(foreignRates: TravelForeignRatesConfig | undefined): string[] {
  const codes = new Set<string>(['DE']);
  if (foreignRates) {
    for (const entry of foreignRates.entries) {
      codes.add(entry.countryCode);
    }
  }
  return Array.from(codes).sort();
}

/**
 * Region-Optionen für ein gewähltes Land (BMF-Städte-Differenzierung, §13.1 E10). Leer, wenn
 * das Land keine Regions-Einträge hat — die Region-Spalte zeigt dann „—" statt einer Auswahl.
 */
function buildRegionOptions(
  foreignRates: TravelForeignRatesConfig | undefined,
  countryCode: string,
): Array<{ regionKey: string; regionLabel: string }> {
  if (!foreignRates || countryCode === 'DE') return [];
  const byKey = new Map<string, string>();
  for (const entry of foreignRates.entries) {
    if (entry.countryCode === countryCode && entry.regionKey) {
      byKey.set(entry.regionKey, entry.regionLabel ?? entry.regionKey);
    }
  }
  return Array.from(byKey.entries())
    .map(([regionKey, regionLabel]) => ({ regionKey, regionLabel }))
    .sort((a, b) => a.regionLabel.localeCompare(b.regionLabel, 'de'));
}

/** Entfernt `regionKey` beim Bau eines neuen Tages-Objekts (typsicher, kein `delete`/Destructuring-Unused). */
function withoutRegion(day: TravelDayRecord): TravelDayRecord {
  return {
    dateISO: day.dateISO,
    countryCode: day.countryCode,
    breakfastProvided: day.breakfastProvided,
    lunchProvided: day.lunchProvided,
    dinnerProvided: day.dinnerProvided,
    overnight: day.overnight,
  };
}

const MS_PER_DAY = 86_400_000;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Enumeriert alle Kalendertage `[startISO, endISO]` inklusive (UTC-Mitternacht,
 * keine Zeitzonen-Fallstricke — identisches Grundprinzip wie der Kern-Parser
 * in `travelAllowance.ts`). Liefert `[]` bei ungültigen/fehlenden Daten oder
 * `end < start` — der Aufrufer belässt `days[]` dann unverändert.
 */
function enumerateDatesInclusive(startISO: string, endISO: string): string[] {
  if (!ISO_DATE_RE.test(startISO) || !ISO_DATE_RE.test(endISO)) {
    return [];
  }
  const start = Date.parse(`${startISO}T00:00:00.000Z`);
  const end = Date.parse(`${endISO}T00:00:00.000Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return [];
  }
  const dates: string[] = [];
  for (let t = start; t <= end; t += MS_PER_DAY) {
    dates.push(new Date(t).toISOString().slice(0, 10));
  }
  return dates;
}

function formatDayLabel(dateISO: string): string {
  const date = new Date(`${dateISO}T00:00:00.000Z`);
  return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' });
}

const checkboxCellClass = 'px-2 py-2 text-center';

/** Gesetzlich fixe Mahlzeitenkürzungs-Prozente (§9 Abs.4a S.8 EStG) — für den Prozent-only-Fallback (RK-17). */
const MEAL_REDUCTION_PCT = { breakfast: 20, lunch: 40, dinner: 40 } as const;

function formatMealReductionCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

/**
 * Baut den `title`-Tooltip-Text für eine Mahlzeiten-Checkbox (RK-17, §14.4): zeigt den
 * konkreten Tages-Kürzungsbetrag, falls `preview` auflösbar ist — sonst fail-closed nur die
 * gesetzlich fixe Kürzungs-Prozentzahl der jeweiligen Mahlzeit.
 */
function buildMealReductionTooltip(
  t: TFunction,
  preview: MealReductionPreviewCents | null,
  mealKey: keyof typeof MEAL_REDUCTION_PCT
): string {
  if (preview === null) {
    return t('days.mealReductionTooltipPctOnly', { pct: MEAL_REDUCTION_PCT[mealKey] });
  }
  const cents =
    mealKey === 'breakfast' ? preview.breakfastCents : mealKey === 'lunch' ? preview.lunchCents : preview.dinnerCents;
  return t('days.mealReductionTooltip', { amount: formatMealReductionCents(cents) });
}

/**
 * Tabelle der Reisetage mit Mahlzeiten-/Übernachtungs-Checkboxen. Regeneriert
 * `days[]` automatisch, sobald sich das Ab-/Rückreisedatum ändert (bereits
 * erfasste Tage innerhalb des neuen Zeitraums bleiben dabei erhalten).
 */
const TripDayMealsTable: React.FC<TripDayMealsTableProps> = ({
  departureDateISO,
  returnDateISO,
  days,
  onChange,
  disabled = false,
  foreignEnabled = false,
  foreignRates,
}) => {
  const { t, i18n } = useTranslation('travel');

  const countryOptions = useMemo(() => buildCountryOptions(foreignRates), [foreignRates]);

  // Lokalisierte Ländernamen via Intl.DisplayNames (§13.8 — kein eigenes Übersetzungs-Asset).
  // Fällt defensiv auf den ISO-Code zurück, falls Intl.DisplayNames in der Zielumgebung fehlt
  // (z.B. ältere Testumgebungen) — nie ein harter Fehler wegen fehlender Lokalisierung.
  const regionDisplayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language || 'de'], { type: 'region' });
    } catch {
      return null;
    }
  }, [i18n.language]);

  const countryLabel = (code: string): string => {
    if (!regionDisplayNames) return code;
    try {
      return regionDisplayNames.of(code) ?? code;
    } catch {
      return code;
    }
  };

  // Ref hält den JEWEILS AKTUELLEN days-Stand für den Merge im Effekt unten,
  // ohne dass `days`/`onChange` als Effekt-Dependencies eine Regenerierungs-
  // Schleife auslösen (der Effekt soll NUR auf Datums-Änderungen reagieren).
  const daysRef = useRef(days);
  daysRef.current = days;
  const lastRangeRef = useRef<string>('');

  useEffect(() => {
    const rangeKey = `${departureDateISO}|${returnDateISO}`;
    if (rangeKey === lastRangeRef.current) {
      return;
    }
    lastRangeRef.current = rangeKey;

    const expectedDates = enumerateDatesInclusive(departureDateISO, returnDateISO);
    if (expectedDates.length === 0) {
      // Unvollständige/ungültige Rahmendaten — days[] bewusst unverändert lassen,
      // bis beide Daten gültig sind (kein voreiliges Leeren der Eingaben).
      return;
    }

    const existingByDate = new Map(daysRef.current.map((d) => [d.dateISO, d]));
    const nextDays: TravelDayRecord[] = expectedDates.map((dateISO, index) => {
      const isLast = index === expectedDates.length - 1;
      const existing = existingByDate.get(dateISO);
      return {
        dateISO,
        countryCode: existing?.countryCode ?? 'DE',
        // LANDMINE-FIX (§13.8/§13.10): regionKey MUSS hier mitgeführt werden, sonst wird eine
        // bereits getroffene Land-/Region-Auswahl bei jeder Datums-Änderung still gedroppt.
        // Key nur setzen, wenn vorhanden (kein `regionKey: undefined` materialisieren, Muster
        // aus dem Rechenkern — hält den Vertrag konsistent zum undefined-Strip beim Write, V10).
        ...(existing?.regionKey !== undefined ? { regionKey: existing.regionKey } : {}),
        breakfastProvided: existing?.breakfastProvided ?? false,
        lunchProvided: existing?.lunchProvided ?? false,
        dinnerProvided: existing?.dinnerProvided ?? false,
        // Letzter Tag: overnight MUSS false sein (travelValidation). Für alle
        // anderen Tage defaultet ein NEU erzeugter Tag auf true (typischer
        // Fall: durchgehende Übernachtungskette, Regel 1) — bereits erfasste
        // Tage behalten ihren Wert.
        overnight: isLast ? false : existing?.overnight ?? true,
      };
    });

    const changed =
      nextDays.length !== daysRef.current.length ||
      nextDays.some((d, i) => {
        const prev = daysRef.current[i];
        return (
          !prev ||
          prev.dateISO !== d.dateISO ||
          prev.countryCode !== d.countryCode ||
          prev.regionKey !== d.regionKey || // LANDMINE-FIX: Teil des changed-Vergleichs (§13.10)
          prev.breakfastProvided !== d.breakfastProvided ||
          prev.lunchProvided !== d.lunchProvided ||
          prev.dinnerProvided !== d.dinnerProvided ||
          prev.overnight !== d.overnight
        );
      });
    if (changed) {
      onChange(nextDays);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDateISO, returnDateISO]);

  const updateDay = (index: number, patch: Partial<TravelDayRecord>): void => {
    const next = days.map((d, i) => (i === index ? { ...d, ...patch } : d));
    onChange(next);
  };

  /**
   * Land-Wechsel eines Tages. Setzt eine ggf. gewählte Region IMMER zurück — anders als der
   * Datums-Regenerierungs-Merge oben (der eine bestehende Region bewusst erhält) ist ein
   * manueller Land-Wechsel ein aktiver Nutzer-Eingriff: die alte Region gehört fast immer zum
   * vorherigen Land und würde sonst als „Geister-Auswahl" unsichtbar weiterbestehen.
   */
  const updateDayCountry = (index: number, countryCode: string): void => {
    const next = days.map((d, i) => (i === index ? { ...withoutRegion(d), countryCode } : d));
    onChange(next);
  };

  /** Region-Wechsel eines Tages. Leerer Wert (`''`, „im Übrigen") entfernt `regionKey` wieder. */
  const updateDayRegion = (index: number, regionKey: string): void => {
    const next = days.map((d, i) => {
      if (i !== index) return d;
      return regionKey === '' ? withoutRegion(d) : { ...withoutRegion(d), regionKey };
    });
    onChange(next);
  };

  if (days.length === 0) {
    return (
      <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline rounded-xl p-4 md:p-5">
        <h2 className="text-sm font-semibold text-primary mb-2">{t('days.title')}</h2>
        <p className="text-xs text-faint">{t('days.emptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline rounded-xl p-4 md:p-5">
      <h2 className="text-sm font-semibold text-primary mb-1">{t('days.title')}</h2>
      <p className="text-xs text-faint mb-1">{t('days.overnightHint')}</p>
      <p className="text-xs text-faint mb-3">{t('days.mealsProvidedHint')}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-faint text-[11px] uppercase tracking-wide">
              <th className="px-2 py-1" />
              {foreignEnabled && <th className="px-2 py-1" />}
              {foreignEnabled && <th className="px-2 py-1" />}
              <th colSpan={3} className="px-2 py-1 text-center font-medium">
                {t('days.mealsProvidedGroupHeader')}
              </th>
              <th className="px-2 py-1" />
            </tr>
            <tr className="text-muted text-xs uppercase tracking-wide">
              <th className="px-2 py-2 text-left font-medium">{t('days.columns.date')}</th>
              {foreignEnabled && (
                <th className="px-2 py-2 text-left font-medium">{t('days.columns.country', 'Land')}</th>
              )}
              {foreignEnabled && (
                <th className="px-2 py-2 text-left font-medium">{t('days.columns.region', 'Ort/Region')}</th>
              )}
              <th className={checkboxCellClass}>{t('days.columns.breakfast')}</th>
              <th className={checkboxCellClass}>{t('days.columns.lunch')}</th>
              <th className={checkboxCellClass}>{t('days.columns.dinner')}</th>
              <th className={checkboxCellClass}>{t('days.columns.overnight')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-hairline)]">
            {days.map((day, index) => {
              const isLastDay = index === days.length - 1;
              const regionOptions = foreignEnabled ? buildRegionOptions(foreignRates, day.countryCode) : [];
              // RK-17 (§14.4): reine Anzeige-Vorschau, KEINE Rechenkern-Änderung — fail-closed
              // `null`, wenn Satz/Kürzung für dieses Datum/Land nicht auflösbar ist (Tooltip
              // zeigt dann nur die Prozente, buildMealReductionTooltip oben).
              const mealPreview = getMealReductionPreviewCents(
                day.dateISO,
                day.countryCode,
                day.regionKey ?? null,
                foreignRates
              );
              return (
                <tr key={day.dateISO}>
                  <td className="px-2 py-2 text-secondary whitespace-nowrap">{formatDayLabel(day.dateISO)}</td>
                  {foreignEnabled && (
                    <td className="px-2 py-2">
                      <select
                        className="bg-surface border border-hairline rounded px-2 py-1 text-xs text-primary"
                        value={day.countryCode}
                        onChange={(e) => updateDayCountry(index, e.target.value)}
                        disabled={disabled}
                        aria-label={t('days.columns.country', 'Land')}
                      >
                        {countryOptions.map((code) => (
                          <option key={code} value={code}>
                            {countryLabel(code)}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  {foreignEnabled && (
                    <td className="px-2 py-2">
                      {regionOptions.length === 0 ? (
                        <span className="text-faint text-xs">—</span>
                      ) : (
                        <select
                          className="bg-surface border border-hairline rounded px-2 py-1 text-xs text-primary"
                          value={day.regionKey ?? ''}
                          onChange={(e) => updateDayRegion(index, e.target.value)}
                          disabled={disabled}
                          aria-label={t('days.columns.region', 'Ort/Region')}
                        >
                          <option value="">{t('days.regionGeneral', 'im Übrigen')}</option>
                          {regionOptions.map((r) => (
                            <option key={r.regionKey} value={r.regionKey}>
                              {r.regionLabel}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  )}
                  <td className={checkboxCellClass}>
                    <input
                      type="checkbox"
                      checked={day.breakfastProvided}
                      onChange={(e) => updateDay(index, { breakfastProvided: e.target.checked })}
                      disabled={disabled}
                      aria-label={t('days.columns.breakfast')}
                      title={buildMealReductionTooltip(t, mealPreview, 'breakfast')}
                    />
                  </td>
                  <td className={checkboxCellClass}>
                    <input
                      type="checkbox"
                      checked={day.lunchProvided}
                      onChange={(e) => updateDay(index, { lunchProvided: e.target.checked })}
                      disabled={disabled}
                      aria-label={t('days.columns.lunch')}
                      title={buildMealReductionTooltip(t, mealPreview, 'lunch')}
                    />
                  </td>
                  <td className={checkboxCellClass}>
                    <input
                      type="checkbox"
                      checked={day.dinnerProvided}
                      onChange={(e) => updateDay(index, { dinnerProvided: e.target.checked })}
                      disabled={disabled}
                      aria-label={t('days.columns.dinner')}
                      title={buildMealReductionTooltip(t, mealPreview, 'dinner')}
                    />
                  </td>
                  <td className={checkboxCellClass}>
                    {isLastDay ? (
                      <span className="text-faint text-xs" title={t('days.lastDayNoOvernightHint')}>
                        —
                      </span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={day.overnight}
                        onChange={(e) => updateDay(index, { overnight: e.target.checked })}
                        disabled={disabled}
                        aria-label={t('days.columns.overnight')}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TripDayMealsTable;
